import { useCallback, useEffect, useState } from 'react';

import { Shell } from '../componentes/Shell.jsx';
import { guardarConConexion } from '../nucleo/api/cliente.js';
import { cambiarEstadoDeMoneda, consultarMonedas } from '../nucleo/api/nucleoApi.js';
import AvisoDeError from '../nucleo/avisos/AvisoDeError.jsx';
import ConfirmacionGeneral from '../nucleo/componentes/ConfirmacionGeneral.jsx';
import '../estilos/catalogos-con-confirmacion.css';

/**
 * Monedas (RF-005, RN-002, RN-004, RN-041).
 *
 * Catálogo CERRADO: se activa o desactiva, no se dan de alta otras. Edición
 * abierta a cualquiera de las dos personas (RN-035).
 *
 * ASIMETRÍA DELIBERADA (RN-042):
 *   * DESACTIVAR valida duro (RN-004) y, si procede, pide confirmación.
 *   * REACTIVAR se ejecuta de inmediato, sin diálogo, con una confirmación
 *     posterior discreta y opción de deshacer (RN-041).
 *
 * ---------------------------------------------------------------------------
 * QUÉ CAMBIÓ EN EL INCREMENTO 16 (RF-225, RF-227, RN-633 a RN-640) Y QUÉ NO
 *
 * La confirmación ya NO se dibuja como una tarjeta más del flujo del documento
 * —el patrón del defecto H-09— sino con el componente único del núcleo, con
 * posición fija, cortina y foco dentro. Y el acuse —y el error, si falla— se
 * presentan ANCLADOS EN EL SITIO DEL RENGLÓN, no en una franja al principio de la
 * pantalla (RN-639, D-348, herencia de RN-505 ya construida en Categorías).
 *
 * LO QUE NO SE TOCÓ, Y ES A PROPÓSITO (RN-633):
 *   * El flujo de DOS PASOS con el servidor. El primer toque viaja SIN confirmar;
 *     el diálogo nace del conflicto que responde el servidor. La salvaguarda es
 *     suya, no del cliente (RN-037).
 *   * Los dos bloqueos duros (moneda predeterminada de alguien activo; cuentas
 *     activas que la usan) NO abren confirmación y se siguen mostrando con el
 *     aviso de error único ARRIBA, con el mensaje completo del servidor, que ya
 *     dice qué cambiar y a quién pedírselo (RN-640).
 *   * El «Deshacer» de reactivar llama a la baja YA CONFIRMADA y no pregunta: es
 *     la vuelta atrás inmediata de RN-041, no una baja nueva (RN-635).
 *   * La cadena del servidor. Su mensaje de confirmación afirma «Hay movimientos
 *     históricos» sin comprobarlo (hallazgo D-339): por eso el diálogo escribe su
 *     propio texto y NO hereda esa afirmación. Corregir el mensaje del servidor es
 *     vuelta aparte (D-219).
 * ---------------------------------------------------------------------------
 */

/*
 * TEXTOS NORMATIVOS DE RN-634. Van exportados y en un solo sitio para que se
 * afirmen carácter por carácter y para que nadie los reescriba en dos lugares.
 *
 * La CONSECUENCIA es un cambio de texto declarado (D-339), no una transcripción:
 * el mensaje del servidor viaja con «¿Continuar?» al final —que aquí quedaría
 * redundante debajo de «Sí, desactivar» y «Cancelar», mismo criterio que D-284— y
 * afirma que hay movimientos históricos sin haberlo comprobado. Lo que esta frase
 * dice es verdadero haya movimientos o no.
 */
export const TITULO_DE_LA_BAJA = (codigoISO) => `¿Desactivar ${codigoISO}?`;

export const CONSECUENCIA_DE_LA_BAJA = (codigoISO) =>
  `Si la desactivas, ya no podrá elegirse al crear cuentas nuevas. Lo ya guardado en ${codigoISO} se conserva.`;

export const ACUSE_DE_LA_BAJA = (codigoISO) => `Moneda ${codigoISO} desactivada.`;

export const ACUSE_DE_LA_REACTIVACION = (codigoISO) => `Moneda ${codigoISO} reactivada.`;

export const ROTULO_DE_ACEPTAR_LA_BAJA = 'Sí, desactivar';
export const ROTULO_MIENTRAS_DESACTIVA = 'Desactivando…';

export default function Monedas() {
  const [monedas, setMonedas] = useState([]);
  const [error, setError] = useState(null);
  const [porConfirmar, setPorConfirmar] = useState(null);
  const [desactivando, setDesactivando] = useState(false);

  /*
   * EL ANCLA: dónde se pinta el acuse o el error de la última acción (RN-639).
   * Guarda el renglón sobre el que se actuó —su identificador y su posición— para
   * poder pintar el aviso EN SU SITIO. Es la misma pieza de estado que Categorías
   * (`Categorias.jsx`), no un invento nuevo.
   */
  const [ancla, setAncla] = useState(null);

  const cargar = useCallback(async () => {
    try {
      // Sin lista no se pinta lista: el catálogo es cerrado y una respuesta que no
      // llega no se disfraza de catálogo vacío ni rompe el recorrido del renglón.
      setMonedas(await consultarMonedas(false) ?? []);
    } catch (fallo) {
      setError(fallo);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const limpiarAvisos = () => { setError(null); setAncla(null); };

  const enviarBaja = (moneda, confirmado) => guardarConConexion(
    () => cambiarEstadoDeMoneda(moneda.idMoneda, false, { confirmado, version: moneda.version }),
  );

  /*
   * PRIMER PASO · la petición viaja SIN confirmar (RN-633). Tres desenlaces:
   *   * el servidor pide confirmación  -> se abre el componente;
   *   * el servidor bloquea duro       -> aviso de error único ARRIBA, sin
   *                                       confirmación de balde (RN-640);
   *   * el servidor acepta sin pedirla -> queda desactivada y se acusa.
   */
  async function pedirBaja(moneda, indice) {
    limpiarAvisos();
    try {
      await enviarBaja(moneda, false);
      setPorConfirmar(null);
      setAncla({ idMoneda: moneda.idMoneda, indice, exito: ACUSE_DE_LA_BAJA(moneda.codigoISO) });
      await cargar();
    } catch (fallo) {
      if (fallo.codigo === 'CONFIRMACION_REQUERIDA') {
        setPorConfirmar({ moneda, indice });
      } else {
        // Bloqueo duro: no hay confirmación que lo salte (RN-004, RN-640).
        setPorConfirmar(null);
        setError(fallo);
      }
    }
  }

  /*
   * SEGUNDO PASO · la pantalla ejecuta la operación; el componente NO la ejecuta y
   * NO se cierra solo (§7 bis). Entre el toque y la respuesta la pregunta sigue
   * abierta «en curso» y el botón de aceptar deja de admitir un segundo toque
   * (RN-638, RN-499).
   */
  async function confirmarBaja() {
    if (desactivando) return;
    const { moneda, indice } = porConfirmar;
    setDesactivando(true);
    try {
      await enviarBaja(moneda, true);
      setPorConfirmar(null);
      setAncla({ idMoneda: moneda.idMoneda, indice, exito: ACUSE_DE_LA_BAJA(moneda.codigoISO) });
      await cargar();
    } catch (fallo) {
      // RN-639 / D-348 · FORMA ÚNICA DE FALLO: la pregunta se cierra, el error se
      // presenta con el aviso único del 08 en el sitio del renglón y la moneda
      // SIGUE activa. No hay quinto estado del componente.
      setPorConfirmar(null);
      setAncla({ idMoneda: moneda.idMoneda, indice, error: fallo });
    } finally {
      setDesactivando(false);
    }
  }

  async function reactivar(moneda, indice) {
    limpiarAvisos();
    try {
      await guardarConConexion(() => cambiarEstadoDeMoneda(moneda.idMoneda, true, { version: moneda.version }));
      setAncla({
        idMoneda: moneda.idMoneda,
        indice,
        exito: ACUSE_DE_LA_REACTIVACION(moneda.codigoISO),
        deshacer: moneda,
      });
      await cargar();
    } catch (fallo) {
      setAncla({ idMoneda: moneda.idMoneda, indice, error: fallo });
    }
  }

  /* RN-635 · Deshacer la reactivación es la baja YA CONFIRMADA: no pregunta. */
  async function deshacerReactivacion(moneda, indice) {
    const actual = monedas.find((m) => m.idMoneda === moneda.idMoneda) ?? moneda;
    limpiarAvisos();
    try {
      await enviarBaja(actual, true);
      setAncla({ idMoneda: actual.idMoneda, indice, exito: ACUSE_DE_LA_BAJA(actual.codigoISO) });
      await cargar();
    } catch (fallo) {
      setAncla({ idMoneda: actual.idMoneda, indice, error: fallo });
    }
  }

  /*
   * Dónde va el ancla: primero se busca el renglón por su identificador y, si no
   * está, se usa la posición que ocupaba, que es donde la persona tenía el dedo.
   * El catálogo es cerrado y ninguna moneda desaparece de la lista, así que el
   * segundo caso es defensa, no conducta esperada.
   */
  let posicionDelAncla = -1;
  if (ancla) {
    const encontrado = monedas.findIndex((m) => m.idMoneda === ancla.idMoneda);
    posicionDelAncla = encontrado >= 0 ? encontrado : Math.min(ancla.indice, monedas.length);
  }

  const anclaPintada = ancla && (
    ancla.error
      ? (
        <AvisoDeError
          key="ancla"
          error={ancla.error}
          margen="0 0 10px"
          alCerrar={() => setAncla(null)}
          datoDePrueba="aviso-de-error-de-la-accion"
        />
      )
      : (
        <div
          key="ancla"
          className="aviso n ancla-de-la-accion"
          role="status"
          data-prueba="acuse-de-la-accion"
          data-estado="exito"
        >
          <button
            type="button"
            className="ancla-de-la-accion__cerrar"
            onClick={() => setAncla(null)}
            aria-label="Cerrar el aviso"
          >
            ✕
          </button>
          ✔ {ancla.exito}
          {ancla.deshacer && (
            <div className="ancla-de-la-accion__deshacer">
              <button
                type="button"
                className="btn sec"
                onClick={() => deshacerReactivacion(ancla.deshacer, ancla.indice)}
                data-prueba="accion-deshacer"
              >
                Deshacer
              </button>
            </div>
          )}
        </div>
      )
  );

  return (
    <Shell titulo="Monedas" volverA="/ajustes">
      {/* Los bloqueos duros llegan aquí: arriba, con el mensaje completo del
          servidor y sin haber preguntado de balde (RN-640). */}
      <AvisoDeError error={error} />

      <div className="lista-con-anclas" data-prueba="lista-de-monedas">
        {monedas.flatMap((moneda, indice) => {
          const renglon = (
            <div
              key={moneda.idMoneda}
              className={`renglon-con-acciones${moneda.activo ? '' : ' renglon-con-acciones--inactivo'}`}
              data-prueba="renglon-de-moneda"
              data-estado={moneda.activo ? 'activa' : 'desactivada'}
              data-moneda={moneda.codigoISO}
            >
              {/* LÍNEA 1 · IDENTIDAD. Ningún control vive aquí. */}
              <div className="renglon-con-acciones__identidad">
                <div className="punto" style={{ background: 'var(--lnk)' }} aria-hidden="true">{moneda.simbolo}</div>
                <div className="crece renglon-con-acciones__texto">
                  <b data-prueba="nombre-de-la-moneda">{moneda.codigoISO} · {moneda.nombre}</b>
                  <small>{moneda.decimales} decimales</small>
                </div>
                <span
                  className={`chip${moneda.activo ? '' : ' w'}`}
                  data-prueba="estado-de-la-moneda"
                >
                  {moneda.activo ? 'Activa' : 'Desactivada'}
                </span>
              </div>

              {/* LÍNEA 2 · LA ACCIÓN. A 320 px no cabe junto al nombre. */}
              <div className="renglon-con-acciones__acciones">
                {moneda.activo
                  ? (
                    <button
                      type="button"
                      className="btn sec"
                      onClick={() => pedirBaja(moneda, indice)}
                      data-prueba="accion-desactivar"
                    >
                      Desactivar
                    </button>
                  )
                  : (
                    <button
                      type="button"
                      className="btn sec"
                      onClick={() => reactivar(moneda, indice)}
                      data-prueba="accion-reactivar"
                    >
                      Reactivar
                    </button>
                  )}
              </div>
            </div>
          );

          return indice === posicionDelAncla ? [anclaPintada, renglon] : [renglon];
        })}

        {ancla && posicionDelAncla >= monedas.length && anclaPintada}
      </div>

      {/*
        * RF-225 · La pregunta la presenta el componente único del núcleo, con
        * posición fija, cortina y foco dentro. Los textos los pone esta pantalla,
        * ya resueltos en castellano (§7 bis de `analisis/requerimientos-categorias.md`).
        * Este flujo no pasa detalle ni advertencia: no hay desglose por módulo que
        * mostrar, la salvaguarda es del servidor.
        */}
      <ConfirmacionGeneral
        abierta={porConfirmar !== null}
        pregunta={porConfirmar ? TITULO_DE_LA_BAJA(porConfirmar.moneda.codigoISO) : ''}
        consecuencia={porConfirmar ? CONSECUENCIA_DE_LA_BAJA(porConfirmar.moneda.codigoISO) : ''}
        rotuloDeAceptar={ROTULO_DE_ACEPTAR_LA_BAJA}
        rotuloEnCurso={ROTULO_MIENTRAS_DESACTIVA}
        enCurso={desactivando}
        alAceptar={confirmarBaja}
        alCancelar={() => setPorConfirmar(null)}
        datoDePrueba="confirmacion-de-baja-de-moneda"
      />

      <div className="aviso i">
        El catálogo es cerrado: solo MXN y USD, y <b>no existe conversión ni tipo de cambio</b>. Los
        totales se presentan en bloques separados por moneda y nunca se suman.
      </div>

      {/* Fila 17 del inventario del incremento 15 (RF-222): salió la justificación del
          diseño («es una operación no destructiva»). El recuadro se conserva tal cual:
          es la anticipación en prosa de los dos bloqueos duros (RN-640). */}
      <div className="info">
        No se puede desactivar una moneda que sea la predeterminada de alguien activo o que use
        alguna cuenta activa: primero se cambia la moneda del usuario o de la cuenta. Si es
        la predeterminada de la otra persona, solo ella puede cambiarla desde su perfil.
        <b data-prueba="monedas-reactivar"> Reactivar es inmediato y no pide confirmación.</b>
      </div>
    </Shell>
  );
}
